import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users, TrendingUp, DollarSign, Briefcase, Network,
} from "lucide-react";
import { BRL } from "@/lib/format";
import WorkforceIndicatorsCard from "@/components/WorkforceIndicatorsCard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

export default function GroupDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/group-consolidated")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const g = data?.group_totals;
  const byCompany = data?.per_company || [];
  const topSectors = data?.top_sectors || [];

  return (
    <div className="space-y-6" data-testid="group-dashboard-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">
          Visão consolidada do Grupo ALCON
        </div>
        <h1 className="font-display text-3xl font-semibold">Dashboard do Grupo</h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          Comparativo executivo entre as empresas do grupo — headcount, turnover, folha e ocupação.
        </p>
      </div>

      {/* Group KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {loading || !g ? [1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-[100px]" />) : (
          <>
            <K label="Ativos" value={g.ativos.toLocaleString("pt-BR")} icon={Users} tone="primary" />
            <K label="Total (com desligados)" value={g.colaboradores.toLocaleString("pt-BR")} icon={Users} />
            <K label="Quadro aprovado" value={g.quadro_aprovado.toLocaleString("pt-BR")} icon={Briefcase} />
            <K label="Ocupação" value={`${g.ocupacao_pct}%`} icon={TrendingUp} tone="accent" />
            <K label="Turnover 12m" value={`${g.turnover_12m_pct}%`} icon={TrendingUp} tone="danger" />
            <K label="Folha mensal" value={BRL(g.folha_mensal)} icon={DollarSign} tone="success" />
          </>
        )}
      </div>

      {/* Composição consolidada por tipo de contrato — TODAS empresas */}
      <WorkforceIndicatorsCard
        companyId={null}
        title="Composição do Grupo por Forma de Contratação"
        testId="group-workforce"
      />

      {/* Per-company comparison chart */}
      <div className="rounded-lg border border-border bg-white p-5">
        <div className="flex items-center gap-2 mb-4">
          <Network className="h-4 w-4 text-[#C5A059]" />
          <h2 className="font-display text-lg font-semibold">Comparativo entre empresas</h2>
        </div>
        <div className="h-[280px]">
          {loading ? <Skeleton className="h-full" /> : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byCompany} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6DA" />
                <XAxis dataKey="nome_fantasia" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="colaboradores_ativos" name="Ativos" fill="#234935" radius={[4,4,0,0]} />
                <Bar dataKey="quadro_aprovado" name="Quadro aprovado" fill="#C5A059" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Per-company details */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-secondary/40">
          <h3 className="font-display text-[15px] font-semibold">Indicadores por empresa</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Empresa</TableHead>
              <TableHead className="text-right">Ativos</TableHead>
              <TableHead className="text-right">Quadro</TableHead>
              <TableHead className="text-right">Ocupação</TableHead>
              <TableHead className="text-right">Turnover 12m</TableHead>
              <TableHead className="text-right">Folha mensal</TableHead>
              <TableHead className="text-right">Vagas</TableHead>
              <TableHead className="text-right">Setores</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [1,2,3].map(i => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-5" /></TableCell></TableRow>)
              : byCompany.map(c => (
                <TableRow key={c.id} data-testid={`gc-row-${c.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-[#8A6B2B]" />
                      {c.nome_fantasia}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{c.colaboradores_ativos}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{c.quadro_aprovado}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={
                      c.ocupacao_pct >= 100 ? "bg-amber-50 text-amber-700 border-amber-200"
                      : c.ocupacao_pct >= 85 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : c.ocupacao_pct >= 60 ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-red-50 text-red-700 border-red-200"
                    }>{c.ocupacao_pct}%</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{c.turnover_12m_pct}%</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{BRL(c.folha_mensal)}</TableCell>
                  <TableCell className="text-right font-mono">{c.vagas_abertas}</TableCell>
                  <TableCell className="text-right font-mono">{c.setores_count}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {/* Top sectors */}
      <div className="rounded-lg border border-border bg-white p-5">
        <h3 className="font-display text-[15px] font-semibold mb-3">Top 10 setores por headcount (Grupo)</h3>
        <div className="space-y-2">
          {loading ? <Skeleton className="h-24" /> :
            topSectors.map(s => (
              <div key={`${s.company}-${s.sector}`} className="flex items-center gap-3 text-[13px]">
                <span className="flex-1 font-medium">{s.sector}</span>
                <span className="text-[11px] text-muted-foreground">{s.company}</span>
                <div className="w-48 h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#234935]"
                       style={{ width: `${Math.min(100, (s.count / topSectors[0]?.count) * 100)}%` }} />
                </div>
                <span className="font-mono tabular-nums w-10 text-right">{s.count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function K({ label, value, icon: Icon, tone = "default" }) {
  const tones = {
    default: "bg-white", primary: "bg-[#234935] text-white",
    success: "bg-emerald-50", accent: "bg-[#F9F4E7]", danger: "bg-red-50",
  };
  const labelColor = tone === "primary" ? "text-white/70" : "text-muted-foreground";
  const valueColor = tone === "primary" ? "text-white" : "text-foreground";
  return (
    <div className={`rounded-lg border border-border ${tones[tone]} p-4`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.22em] font-medium ${labelColor}`}>{label}</div>
          <div className={`mt-1 font-display text-[22px] font-semibold ${valueColor}`}>{value}</div>
        </div>
        <Icon className={`h-4 w-4 shrink-0 ${tone === "primary" ? "text-[#C5A059]" : "text-muted-foreground"}`} />
      </div>
    </div>
  );
}
